import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useCreateManualCreditNote,
  useGetAccountPlanSchedule,
  getListAccountCreditNotesQueryKey,
  getGetAccountPlanScheduleQueryKey,
  type Membership,
} from "@workspace/api-client-react";
import { Sparkles } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";

// Manual credit-note CTA for an early-exit / settlement scenario.
//
// The operator types the GST-INCLUSIVE amount to credit + a reason. The
// server splits taxable + GST at the membership plan's rate (proper §34
// credit note), references the latest paid invoice as the source, and
// generates the PDF. Different from the full Restructure flow — this is
// the simple "issue one credit note" path.

interface Props {
  accountId: number;
  membership: Membership | null;
  planName: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ManualCreditNoteDialog({
  accountId,
  membership,
  planName,
  open,
  onOpenChange,
}: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const createMutation = useCreateManualCreditNote();
  // Reuses the schedule already cached by the page that mounts this dialog
  // — no extra fetch in the typical "Plan tab → Credit Note" path.
  const { data: schedule } = useGetAccountPlanSchedule(accountId, {
    query: {
      enabled: open && membership != null,
      queryKey: getGetAccountPlanScheduleQueryKey(accountId),
    },
  });
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");

  // Reset when the dialog closes so the next open starts fresh.
  useEffect(() => {
    if (!open) {
      setAmount("");
      setReason("");
    }
  }, [open]);

  // Pro-rata of unused days against the latest Paid period for this
  // membership. Suggestion only — operator can ignore and type any
  // amount. Returns null when the math can't be computed (no paid
  // period, no exit date, exit outside the period window).
  const proRata = useMemo(() => {
    if (!membership || !schedule) return null;
    const endDate = membership.endDate;
    if (!endDate) return null;
    const periods = schedule.periods ?? [];
    // Latest Paid period for this membership.
    const paid = periods
      .filter((p) => p.membershipId === membership.id && p.status === "Paid")
      .sort((a, b) => b.periodStart.localeCompare(a.periodStart))[0];
    if (!paid) return null;
    if (endDate < paid.periodStart || endDate >= paid.periodEnd) return null;

    // Inclusive day counts: a 1 May → 31 May period is 31 days.
    const dayMs = 86_400_000;
    const start = new Date(paid.periodStart).getTime();
    const end = new Date(paid.periodEnd).getTime();
    const exit = new Date(endDate).getTime();
    const totalDays = Math.round((end - start) / dayMs) + 1;
    const usedDays = Math.round((exit - start) / dayMs) + 1;
    const unusedDays = Math.max(0, totalDays - usedDays);
    if (unusedDays === 0) return null;
    const unusedAmount = Math.round(Number(paid.amount) * (unusedDays / totalDays) * 100) / 100;
    return {
      periodAmount: Number(paid.amount),
      totalDays,
      usedDays,
      unusedDays,
      unusedAmount,
      periodStart: paid.periodStart,
      periodEnd: paid.periodEnd,
    };
  }, [membership, schedule]);

  async function save() {
    if (!membership) return;
    const n = Number(amount);
    if (!Number.isFinite(n) || n <= 0) {
      toast({ title: "Enter a valid amount.", variant: "destructive" });
      return;
    }
    try {
      const result = await createMutation.mutateAsync({
        id: accountId,
        data: {
          membershipId: membership.id,
          amount: n,
          ...(reason.trim() ? { reason: reason.trim() } : {}),
        },
      });
      queryClient.invalidateQueries({ queryKey: getListAccountCreditNotesQueryKey(accountId) });
      queryClient.invalidateQueries({ queryKey: getGetAccountPlanScheduleQueryKey(accountId) });
      toast({
        title: `Credit note ${result.creditNoteNumber} issued.`,
        description: result.pdfUrl ? "PDF ready to download." : "PDF rendering in background.",
      });
      onOpenChange(false);
    } catch (err) {
      const msg = (err as Error)?.message ?? "Please try again.";
      toast({
        title: "Couldn't issue the credit note.",
        description: msg,
        variant: "destructive",
      });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Issue Credit Note</DialogTitle>
          <DialogDescription>
            {planName
              ? `Manual credit against ${planName}. Server applies GST split at the plan rate (§34 credit note).`
              : "Manual credit note for early-exit / settlement."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs">
              Amount (₹, GST-inclusive) <span className="text-destructive">*</span>
            </Label>
            <Input
              type="number"
              inputMode="decimal"
              min={0}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="e.g. 21240"
              autoFocus
            />
            {proRata ? (
              <div className="flex items-center justify-between rounded-md border border-dashed bg-muted/40 px-2 py-1.5">
                <div className="text-[11px] text-muted-foreground">
                  Suggested pro-rata: <span className="font-medium text-foreground">₹{proRata.unusedAmount.toLocaleString("en-IN")}</span>{" "}
                  ({proRata.unusedDays} of {proRata.totalDays} days unused on the last paid period)
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={() => setAmount(String(proRata.unusedAmount))}
                >
                  <Sparkles className="mr-1 h-3 w-3" /> Use
                </Button>
              </div>
            ) : null}
            <p className="text-[11px] text-muted-foreground">
              Enter the total credit amount as agreed. The server splits taxable
              + CGST/SGST/IGST at the plan's rate so your GSTR-1 credit-notes
              section foots correctly.
            </p>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Reason (optional, printed on PDF)</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="e.g. Early exit on 30 Jun 2026 against May–Aug invoice"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={createMutation.isPending}>
            Cancel
          </Button>
          <Button onClick={save} disabled={createMutation.isPending}>
            {createMutation.isPending ? "Issuing…" : "Issue Credit Note"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
